<template>
    <component ref="dropdown" :is="dropdownComponent" class="ur-auto-dropdown" :items="filteredItems" v-model="dropdownValue" @input="onSelected" v-bind="dropdownProps">
        <template #selected>
            <ur-text-field v-model="inputText"  :disabled="disabled" :placeholder="cInputPlaceholder" v-bind="textProps" />
        </template>
    </component>
</template>

<script>
    import _ from 'lodash'
    import urValidatorMixin from '../../../mixins/urValidator/urValidatorMixin.vue';
    import urDropdown            from "../urDropdown/urDropdown";
    import urMultiSelectDropdown from "../urMultiSelectDropDown/urMultiSelectDropdown";

    const UNSELECT_DROPDOWN_TITLE = "Select";
    const DEFAULT_PLACEHOLDER = "Input value";
    const DEFAULT_ITEM_TEXT  = "text";
    const DEFAULT_ITEM_VALUE = "value";

    const NOT_SELECTED = -1;
    const NEED_CHECK   = -2;

    export default {
        name: "urAutoDropdown",
        mixins: [urValidatorMixin],
        props: {
            // $ver: v0.2.2
            // $hide
            multiSelect : {
                type: Boolean, default: false,
            },
            // $ver: v0.2.2
            // $tags: IDE
            // 선택가능 목록에 대한 data입니다.
            // Array type으로 전달될 경우 각각의 element는 이름과 값을 가지는 Object형태를 가져야합니다.
            // item-text, item-value의 기본값이 'text', 'value'이므로 {text: 'label', value: 'value'} 형태를 사용할 수 있습니다.
            // #The data for the selectable list.
            // #When passed as an array type, each element must have an Object type with a name and a value.
            // #You can use the form {text: 'label', value: 'value'} because the item-text and item-value defaults to 'text' and 'value'.
            items: {
                type:[Array, Object],
                validator: function(val) {
                    return Array.isArray(val);
                },
                default: ()=>[]
            },
            // $ver: v0.2.2
            // $v-model
            // $tags: IDE
            // 드랍다운 리스트의 선택된 값 입니다.
            // #The selected value in the drop-down list.
            value: {
                type:[Object,String,Number]
            },
            // $ver: v0.2.2
            // 목록 필터 로직을 부여합니다.
            // #Gives list filter logic.
            // $tags IDE
            itemFilter : {type: Function},
            // $ver: v0.2.2
            // $default: 'Input value'
            // 아무런 data도 선택되지 않았을 때 기본적으로 출력되는 메시지입니다.
            // #This message is displayed by default when no data is selected.
            placeholder : {type: String, default:'Select'},

            // $ver: v0.2.2
            // $default: 'text'
            // items에 전달된 데이터 Object에서 리스트에 출력될 값을 선택하는 key값입니다.
            // 혹은 함수형태로 전달하여 text build를 수행할 수 있습니다.(v0.2.2 추가됨)
            // #The key value that selects the value to be displayed in the list from the data object passed to items.
            // #Alternatively, you can perform a text build by passing it as a function (Added to v0.2.2).
            itemText    : {
                type: [String, Function],
                default: DEFAULT_ITEM_TEXT
            },
            // $ver: v0.2.2
            // $default: 'value'
            // items에 전달된 데이터 Object에서 item선택시 v-model에 전달될 값을 선택하는 key값입니다.
            // #The key value that selects the value to be passed to the v-model when selecting an item from the data object passed to the items.
            itemValue   : {type: String , default: DEFAULT_ITEM_VALUE},

            // $ver: v0.2.2
            // $tags: IDE
            // <em>ur-dropdown</em>컴포넌트가 펼쳐졌을 때 나오는 item리스트의 width를 지정할 수 있습니다. 단위는 px입니다.
            // #You can specify the width of the item list that appears when the <em>ur-dropdown</em> component is expanded. The unit is px.
            itemWidth   : {type: [Number, String] /*px*/},

            // $ver: v0.2.2
            // $tags: IDE
            // 상호작용 불가능한 상태를 만듭니다.
            // #Create a non-interactive state.
            disabled    : {type: Boolean, default: false},
            // $ver: v0.2.2
            // 하단에 표시되는 가이드 메시지입니다. 별도의 설정이 없다면 출력되지 않습니다.
            // <em>ur-dropdown</em>은 메시지 출력을 위한 공간을 별도로 점유하지않습니다.
            // 메시지 출력을 원할 경우 공간확보가 필요합니다.
            // #The guide message that appears at the bottom. If there is no setting, no output.
            // #<em>ur-dropdown</em> does not occupy space for message output.
            // #If you want to print the message, you need to make space.
            guideMessage: {type: String, default: undefined},
            // $ver: v0.2.2
            // validate를 통과한 경우 출력되는 메시지입니다.
            // 별도설정이 없다면 가이드 메시지가 출력되거나 아무런 메시지도 출력되지 않습니다.
            // <em>ur-dropdown</em>은 메시지 출력을 위한 공간을 별도로 점유하지않습니다.
            // 메시지 출력을 원할 경우 공간확보가 필요합니다.
            // #The message that is output if validate passes.
            // #If there is no setting, guide message or no message is printed.
            // #<em>ur-dropdown</em> does not occupy space for message output.
            // #If you want to print the message, you need to make space.
            successMessage: {type: String, default: undefined},
            // $ver: v0.2.2
            // 가이드 메시지, 에러메시지, 성공메시지의 스타일을 'heavy', 'light' 두가지 스타일 중 선택할 수 있습니다.
            // #You can select the style of guide message, error message or success message from 'heavy' and 'light'.
            messageStyle: {
                type: String,
                validator: function(value) {
                    return ['heavy', 'light'].indexOf(value) !== -1;
                },
                default: 'heavy'
            },
            // $ver: v0.2.2
            // Dropdown의 크기를 결정합니다. String 형으로 small, medium에 대한 IDE 처리를 위해 추가되었습니다.
            //#The size You must enter one of two types: medium or small.
            size      : {type: String, default: undefined},
            // $ver: v0.2.2
            // 기본사이즈의 dropdown 컴포넌트를 사용합니다.
            // #Use the default size dropdown component.
            medium: {
                type: Boolean,
                default: false
            },
            // $ver: v0.2.2
            // $abbr: medium
            md: {
                type:Boolean,
                default: false,
            },
            // $ver: v0.2.2
            // 기본보다 작은 사이즈의 dropdown 컴포넌트를 사용합니다.
            // #Use a smaller dropdown component than the default.
            small: {
                type: Boolean,
                default: false
            },
            // $ver: v0.2.2
            // $abbr: small
            sm: {
                type:Boolean,
                default: false,
            },
            // $ver: v0.2.2
            // 드롭다운의 모양을 결정합니다. String 형으로 border, underline 이라는 값을 가질 수 있습니다.
            // #Express Dropdown as shape such as border, underline.
            // $default: 'border'
            // $tags shape
            shape: { type: String, default: 'border'},
            // $ver: v0.2.2
            // Underline 드롭다운 유형 여부입니다.
            // #Express Dropdown as underline.
            // $default: false
            underline: {type: Boolean, default: false},
            /* [FROM VALIDATOR] */
            // rules
            // disabled
            // lazy
            // noShake
            /********************/
        },
        data() {
            return {
                inputText:'',
                dropdownValue: this.value,
                filteredItems: this.items,
                dropdownProps: {
                    placeholder : this.placeholder,
                    itemText    :this.itemText,
                    itemValue   : this.itemValue,
                    itemWidth   : this.itemWidth,
                    disabled    : this.disabled,
                    guideMessage: this.guideMessage,
                    successMessage: this.successMessage,
                    messageStyle: this.messageStyle,
                    size      : this.size,
                    medium: this.medium,
                    md: this.md,
                    small: this.small,
                    sm: this.sm,
                    shape: this.shape,
                    underline: this.underline,
                    rules: this.rules,
                    lazy: this.lazy,
                    noShake: this.noShake,
                },
                textProps:{
                    // placeholder : this.placeholder,
                    // guideMessage: this.guideMessage,
                    // successMessage: this.successMessage,
                    messageStyle: this.messageStyle,
                    size      : this.size,
                    medium: this.medium,
                    md: this.md,
                    small: this.small,
                    sm: this.sm,
                    // rules: this.rules,
                    lazy: this.lazy,
                    noShake: this.noShake,
                    // tabIndex: this.tabIndex,
                    // placeholder: this.placeholder
                }
            }
        },
        computed : {
            /* [FROM VALIDATOR] */
            // isValidatable
            // el
            validateTarget() {return this.value;}, // validation setting
            cInputPlaceholder() {
                return this.placeholder === 'Select' && this.valueTextMap.get(this.value) ? this.valueTextMap.get(this.value) :  this.placeholder;
            },
            dropdownComponent() {
                //우선은 그냥 dropdown만
                return /*this.multiSelect ? urMultiSelectDropdown :*/ urDropdown;
            },
            itemFilterFunc() {
                if (this.itemFilter && typeof this.itemFilter === 'function') {
                    return this.itemFilter;
                } else {
                    return function (text, val) {
                        return text.toLowerCase().indexOf(val.toLowerCase()) > -1;
                    }
                }
            },
            //value - text map을 만들어놓자
            valueTextMap() {
                let rtn = new Map();
                for(let i = 0 ; i < this.items.length; i++) {
                    let item = this.items[i];
                    let value = typeof item === 'string' ? item : item[this.itemValue];
                    let text = typeof item === 'string' ? item : item[this.itemText];
                    rtn.set(value, text);
                    let children = item.childItems;
                    if(children && children.length > 0){
                        for(let j = 0 ; j < children.length; j++) {
                            let child = children[j];
                            let childValue = typeof child === 'string' ? child : child[this.itemValue];
                            let childText = typeof child === 'string' ? child : child[this.itemText];
                            rtn.set(childValue, childText);
                        }
                    }
                }
                return rtn;
            },
        },
        watch : {
            validateTarget(val) {
                this.validateOnEvent(); // whenever checked value changes, execute validate() if lazy is false
            },
            items: {
                handler: function(newVal, oldVal) {
                    this.filter(this.inputText);
                },
                deep: true
            },
            inputText(newVal, oldVal) {
                this.filter(newVal);
                if(newVal === ''){
                    this.dropdownValue = ''
                }
            },
            dropdownValue(newVal, oldVal) {
                this.$emit('input', newVal)
            },
            value: {
                immediate: true,
                handler(val) {
                    this.dropdownValue = val;
                    this.inputText = this.getText(val);
                }
            },
        },
        created() {

        },
        methods: {
            onSelected(val) {
                if(this.multiSelect){
                    let itemText = [];
                    val.forEach(item => {
                        if (typeof(this.itemText) === 'function') {
                            itemText.push(this.itemText(item));
                        } else {
                            itemText.push(item[this.itemText]);
                        }
                    });
                    this.inputText = itemText.sort().join(", ");
                } else {
                    this.inputText = this.getText(val);
                }
                this.$emit('input', val)
            },
            getText(val){
                return this.valueTextMap.get(val);
            },
            filter(val) {
                let rtn = [];
                if(!val) {
                    this.filteredItems = this.items;
                    return;
                }
                let itemsClone = _.cloneDeep(this.items);
                for(let i = 0 ; i < itemsClone.length; i++) {
                    let item = itemsClone[i];
                    let text = typeof item === 'string' ? item : item[this.itemText];
                    if(this.itemFilterFunc(text, val)) {
                        rtn.push(item);
                        continue;
                    }
                    let children = item.childItems;
                    if(children && children.length > 0){
                        for(let j = children.length - 1 ; j > -1; j--) {
                            let child = children[j];
                            let text = typeof child === 'string' ? child : child[this.itemText];
                            if(!this.itemFilterFunc(text, val)) {
                                children.splice(children.indexOf(child), 1);
                            }
                        }
                        if(children.length > 0){
                            rtn.push(item);
                        }
                    }
                }
                this.filteredItems = rtn;
            },
            /* [FROM VALIDATOR] */
            // validate()        --+
            // isValid()         --+-- exposed methods for manual validate control
            // clearValidation() --+
            // validateOnEvent() -> should be set by each component which uses urValidatorMixin
            /********************/
            // Override
            validate() {
                return this.$refs.dropdown.validate();
            },
            // Override
            validateOnEvent() {
                return this.$refs.dropdown.validateOnEvent();
            },
            // Override
            isValid() {
                return this.$refs.dropdown.isValid();
            },
            // Override
            clearValidation() {
                return this.$refs.dropdown.clearValidation();
            }
        }

    }
</script>
